/**
 * Emissions Tracker Types
 *
 * Enums and config are re-exported from @g17eco/emissions-config (single source of truth).
 * Interfaces specific to the frontend API contract are defined here.
 */

// Import enums for local use in interface definitions below
// (re-exports don't create local bindings in TypeScript)
import { 
  AssetType, 
  Scope2Method, 
  CalculationStatus, 
  TargetFramework, 
  TargetStatus, 
  type ActivityUnit, 
  type CalculationNotice, 
  type DataQuality, 
  type EmissionScope,
  type FuelType,
  type FugitiveGasType,
  type ProcessType,
  type RefrigerantGasType,
  type Scope1ActivityType,
  type Scope2ActivityType,
  type SourceTrailEntry,
  type VehicleType,
} from '@g17eco/emissions-config';

// Re-export all shared enums and config from the package
export {
  AssetType,
  EmissionScope,
  Scope1ActivityType,
  Scope2ActivityType,
  VehicleType,
  RefrigerantGasType,
  FugitiveGasType,
  ProcessType,
  FuelType,
  ActivityUnit,
  DataQuality,
  Scope2Method,
  CalculationStatus,
  CalculationNoticeType,
  TargetFramework,
  TargetStatus,
  ACTIVITY_CONFIG,
  getValidUnits,
  getStationaryFuels,
  getMobileFuels,
  type ActivityTypeWithUnits,
  type ActivityOptions,
  type CalculationNotice,
  type FallbackType,
  type SourceTrailEntry,
  BULK_IMPORT_MAX_ROWS,
  UNIT_CATEGORIES,
  type UnitCategory,
  ASSET_TYPE_LABEL_ENTRIES,
  ASSET_TYPE_LABELS,
} from '@g17eco/emissions-config';
export type ActivityType = Scope1ActivityType | Scope2ActivityType;

// ---------------------------------------------------------------------------
// Frontend-only enums
// ---------------------------------------------------------------------------

// Emission units
export enum EmissionUnit {
  KgCO2e = 'kgCO2e',
  TonnesCO2e = 'tCO2e',
}

export enum EmissionFactorProvider {
  Climatiq = 'service',
  Internal = 'internal',
  Manual = 'manual',
}

export enum BulkImportStatus {
  Processing = 'processing',
  Completed = 'completed',
  PartialFailure = 'partial_failure',
  Failed = 'failed',
}

export enum MarketInstrumentType {
  REC = 'rec',
  PPA = 'ppa',
  GO = 'go',
  SupplierSpecific = 'supplier_specific',
  ResidualMix = 'residual_mix',
}

export enum GeneratedReportStatus {
  Completed = 'completed',
  Processing = 'processing',
  Failed = 'failed',
}

export enum ReportTemplateType {
  GHGProtocol = 'ghg-protocol',
  Summary = 'summary',
  ScopeDetail = 'scope-detail',
  Custom = 'custom',
}

export enum FloorAreaUnit {
  SquareMeters = 'm2',
  SquareFeet = 'ft2',
}

export enum EmployeeCountType {
  FTE = 'fte',
  Headcount = 'headcount',
}

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

// User reference for activity records
export interface ActivityUser {
  _id: string;
  name: string;
}

// Evidence types — mirror apps/emissions/src/models/emissionEvidence.ts
export enum EvidenceType {
  Invoice = 'invoice',
  MeterReading = 'meter_reading',
  UtilityBill = 'utility_bill',
  DeliveryNote = 'delivery_note',
  Certificate = 'certificate',
  AuditReport = 'audit_report',
  ContractOrPPA = 'contract_or_ppa',
  SupplierAttestation = 'supplier_attestation',
  CalculationWorkbook = 'calculation_workbook',
  MethodologyDoc = 'methodology_doc',
  Photo = 'photo',
  Other = 'other',
}

// Evidence data structure following UTRV pattern
export interface EmissionEvidenceData {
  documentId: string;
  description?: string;
  linkedAt?: string;
  linkedBy?: string;
}

// Evidence projection the frontend is allowed to see. Storage internals
// (storagePath, storageProvider, sha256) and audit user ids (uploadedBy,
// deletedBy, lockedBy) stay server-side — downloads go through a signed-URL
// endpoint so the client never needs the bucket path, and lock / delete
// events surface via status flags rather than user attribution.
export interface EmissionEvidence {
  _id: string;
  initiativeId: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  evidenceType: EvidenceType;
  description?: string;
  documentDate?: string;
  periodStart?: string;
  periodEnd?: string;
  issuer?: string;
  tags?: string[];
  lockedAt?: string;
  isDeleted: boolean;
  created: string;
  updated: string;
}

// Library-list rows always carry the gateway-hydrated uploader name and
// the aggregated linked-activity count. Single-record reads (promote
// response, attach result) return `EmissionEvidence` without these.
export interface EmissionEvidenceListItem extends EmissionEvidence {
  uploadedByName: string;
  linkedActivityCount: number;
}

// Slim activity row returned by `GET /evidence/:id/activities`. Mirrors
// the gateway's `EvidenceActivityLink` shape — keep fields in sync.
export interface EvidenceActivityLink {
  _id: string;
  scope: EmissionScope;
  activityType: string;
  name?: string;
  periodStart: string;
  periodEnd: string;
  assetId?: string;
}

// Upload sessions — batch staging for evidence during data entry.
export enum UploadSessionStatus {
  Active = 'active',
  Committed = 'committed',
  Expired = 'expired',
}

/**
 * User-editable evidence classification. Shared by `SessionFile.metadata`
 * and as the PATCH-body shape for `SessionFileMetadataUpdate`; matches the
 * backend's `EvidenceMetadata` type (ISO strings for Date fields on the wire).
 */
export interface EvidenceMetadata {
  evidenceType?: EvidenceType;
  description?: string;
  documentDate?: string;
  periodStart?: string;
  periodEnd?: string;
  issuer?: string;
  tags?: string[];
}

// SessionFile projection — storage internals (sha256, tempStoragePath) stay
// on the server. The frontend only cares about what the file is, its current
// metadata, and whether it has been promoted.
export interface SessionFile {
  fileId: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  metadata: EvidenceMetadata;
  promotedToEvidenceId?: string;
  promotedAt?: string;
}

export interface UploadSession {
  _id: string;
  initiativeId: string;
  status: UploadSessionStatus;
  expiresAt: string;
  files: SessionFile[];
  created: string;
  updated: string;
}

export type SessionFileMetadataUpdate = EvidenceMetadata;

/**
 * Shape returned by both direct upload and session promotion. Discriminated
 * on `attached` so `attachError` is only reachable in the failure case.
 * `attached: false` covers two scenarios:
 *   - no activity was specified (library-style upload)
 *   - an activity was specified but the link failed; `attachError` carries
 *     a user-safe reason. The evidence itself is always persisted.
 */
export type AttachOutcome =
  | { attached: true }
  | { attached: false; attachError?: string };

export type EvidencePersistenceResult = AttachOutcome & {
  evidence: EmissionEvidence;
  dedup: boolean;
};

export type PromoteFileResult = EvidencePersistenceResult;
export type UploadEvidenceResult = EvidencePersistenceResult;

// Activity record
export interface ActivityRecord {
  _id: string;
  initiativeId: string;
  rootInitiativeId: string;
  scope: EmissionScope;
  activityType: ActivityType;
  periodStart: string;
  periodEnd: string;
  quantity: number;
  unit: ActivityUnit;
  fuelType?: FuelType;
  vehicleType?: VehicleType;
  refrigerantType?: RefrigerantGasType | FugitiveGasType;
  gasType?: FugitiveGasType;
  processType?: ProcessType;
  region: string;
  regionFallback?: boolean;
  yearFallback?: boolean;
  dataSource?: string;
  notes?: string;
  name?: string;
  assetId?: string;
  assetName?: string;
  evidenceData?: EmissionEvidenceData[];
  bulkImportId?: string;
  isDeleted: boolean;
  createdBy: ActivityUser | string;
  created: string;
  updated: string;
  // Calculation fields - joined from EmissionCalculation by backend
  dataQuality?: DataQuality;
  calculatedCO2e?: number;
  calculatedLocationBasedCO2e?: number;
  calculatedMarketBasedCO2e?: number;
  calculationStatus?: CalculationStatus;
  calculationNotices?: CalculationNotice[];
  emissionFactorSource?: string;
  scope2Method?: Scope2Method;
}

// Create activity request
export interface CreateActivityRequest {
  scope: EmissionScope;
  activityType: ActivityType;
  periodStart: string;
  periodEnd: string;
  quantity: number;
  unit: ActivityUnit;
  fuelType?: FuelType;
  vehicleType?: VehicleType;
  refrigerantType?: RefrigerantGasType;
  gasType?: FugitiveGasType;
  processType?: ProcessType;
  region: string;
  regionFallback?: boolean;
  yearFallback?: boolean;
  dataSource?: string;
  notes?: string;
  name?: string;
  assetId?: string | null;
  /**
   * Optional Climatiq factor id to use instead of running factor search at
   * calculation time. Bulk-import endpoint reads this per row; single-create
   * paths ignore it (use the override mutation instead).
   */
  climatiqFactorId?: string;
}

// Update activity request
export type UpdateActivityRequest = Partial<CreateActivityRequest>;

// Emission calculation result
export interface EmissionCalculation {
  _id: string;
  activityRecordId: string;
  initiativeId: string;
  rootInitiativeId: string;
  scope: EmissionScope;
  scope2Method?: Scope2Method;
  co2e: number;
  co2eUnit: EmissionUnit;
  co2?: number;
  ch4?: number;
  n2o?: number;
  /** Biogenic CO2 memo item — excluded from scope totals, required for GRI/CSRD */
  biogenicCo2e?: number;
  locationBasedCo2e?: number;
  marketBasedCo2e?: number;
  tdCo2e?: number;
  wttCo2e?: number;
  wttOfTdCo2e?: number;
  auditTrail?: string;
  sourceTrail?: SourceTrailEntry[];
  emissionFactorId?: string;
  emissionFactorValue: number;
  emissionFactorUnit: string;
  emissionFactorProvider?: EmissionFactorProvider;
  emissionFactorSource: string;
  emissionFactorSourceLink?: string;
  emissionFactorSourceDataset?: string;
  emissionFactorYear?: number;
  emissionFactorYearReleased?: number;
  /** Region the factor applies to (may differ from activity.region under fallback) */
  emissionFactorRegion?: string;
  /** Human-readable region name (e.g. 'European Union') */
  emissionFactorRegionName?: string;
  /** Human-friendly factor name (e.g. 'Emission intensity of propane (net CV)') */
  emissionFactorName?: string;
  /** Climatiq taxonomy: e.g. 'Fuel', 'Heat & Steam' */
  emissionFactorCategory?: string;
  /** Climatiq taxonomy: e.g. 'Energy', 'Buildings' */
  emissionFactorSector?: string;
  /** Long-form provenance blurb */
  emissionFactorDescription?: string;
  /** Climatiq unit type, e.g. ['Energy'], ['Weight'] */
  emissionFactorUnitType?: string[];
  climatiqFactorId?: string;
  climatiqActivityId?: string;
  gwpMethodology: string;
  regionFallback?: boolean;
  yearFallback?: boolean;
  dataQuality: DataQuality;
  fallbackType?: 'year' | 'region' | 'region_and_year';
  status: CalculationStatus;
  notices?: CalculationNotice[];
  calculatedAt: string;
  version: number;
  isFactorOverride?: boolean;
  factorOverriddenBy?: string;
  factorOverriddenAt?: string;
  isRevert?: boolean;
  revertedFromVersion?: number;
  revertedBy?: string;
  revertedAt?: string;
}

// Data quality summary (aggregate from backend)
export interface DataQualitySummary {
  measured: number;
  calculated: number;
  calculatedFallback: number;
  estimated: number;
  failed: number;
  withCO2e: number;
  total: number;
}

// Per-gas emission totals (GWP-weighted, kg CO2e)
export interface GasTotals {
  co2: number;
  ch4: number;
  n2o: number;
}

// Emission summary
export interface EmissionSummary {
  totalCO2e: number;
  unit: EmissionUnit;
  totalTonnesCO2e: number;
  byScope: Record<string, number>;
  byGas?: GasTotals;
  byScopeGas?: Record<string, GasTotals>;
  scope2DualReporting?: Scope2DualSummary;
  activityCount: number;
  initiativeCount?: number;
}

// API response with calculation result
export interface ActivityWithCalculation {
  data: ActivityRecord;
  calculation: EmissionCalculation | null;
  notices?: CalculationNotice[];
}

// Pagination
export interface Pagination {
  total: number;
  limit: number;
  offset: number;
}

// Activity list response
export interface ActivityListResponse {
  data: ActivityRecord[];
  pagination: Pagination;
}

// Report query params
export interface ReportQueryParams {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  scopes?: EmissionScope[];
  groupBy?: 'scope' | 'activityType' | 'initiative' | 'month';
  includeChildren?: boolean;
}

// Activity type report
export interface ActivityTypeReport {
  scope: EmissionScope;
  activityType: ActivityType;
  total: number;
  count: number;
}

// Monthly report
export interface MonthlyReport {
  year: number;
  month: number;
  scope: EmissionScope;
  total: number;
  count: number;
}

// Source-period breakdown report
export type SourcePeriodGranularity = 'monthly' | 'quarterly' | 'annual';

export type SourceSubTypeField = 'fuelType' | 'vehicleType' | 'refrigerantType' | 'processType' | null;

export interface SourcePeriodBucket {
  index: number;
  label: string;
  start: string;
  end: string;
}

export interface SourcePeriodRow {
  scope: EmissionScope;
  activityType: ActivityType;
  subTypeField: SourceSubTypeField;
  subTypeValue: string | null;
  /** CO₂e per bucket, prorated by overlap. */
  totals: number[];
  /** Number of activities overlapping each bucket (parallel to `totals`). */
  counts: number[];
  /**
   * Activity ID strings per bucket (parallel to `totals` / `counts`). Used by
   * the asset Data Quality grid so non-gap cell clicks open the right
   * activity (or a small picker when multiple contributed).
   */
  activityIds: string[][];
  grandTotal: number;
  /** Total number of activities in the row. */
  count: number;
}

export interface SourcePeriodReport {
  buckets: SourcePeriodBucket[];
  rows: SourcePeriodRow[];
  unit: string;
  period: { start?: string; end?: string };
}

export interface SourcePeriodParams {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  granularity: SourcePeriodGranularity;
  scopes?: EmissionScope[];
  includeChildren?: boolean;
  /** Restrict the report to a single asset (Asset → Data Quality tab). */
  assetId?: string;
}

// Entity monthly trend data
export interface EntityMonthlyRow {
  entityId: string;
  entityName: string;
  year: number;
  month: number;
  total: number;
}

export interface EntityMonthlyResponse {
  results: EntityMonthlyRow[];
  period: { start: string; end: string };
  groupBy: 'initiative' | 'asset';
}

// Market-based input for Scope 2 dual reporting
export interface MarketBasedInput {
  instrumentType: MarketInstrumentType;
  emissionFactor?: number;
  factorUnit?: string;
  certificateId?: string;
  supplierName?: string;
  contractDetails?: string;
}

// Scope 2 dual calculation result
export interface Scope2DualResult {
  locationBased: EmissionCalculation;
  marketBased: EmissionCalculation;
}

// Supplementary Scope 2/3 energy supply chain emissions.
// These are NOT part of the Scope 2 total — they are separate GHG Protocol disclosures.
export interface Scope2SupplementaryTotals {
  /** Scope 3.3: T&D losses (kgCO2e) */
  tdCo2eTotal: number;
  /** Upstream well-to-tank emissions (kgCO2e) */
  wttCo2eTotal: number;
  /** Well-to-tank of T&D (kgCO2e) */
  wttOfTdCo2eTotal: number;
  /** Biogenic CO2 memo item — excluded from all scope totals (kgCO2e) */
  biogenicCo2eTotal: number;
  unit: string;
}

// Summary returned by GET /reports/scope2-dual
export interface Scope2DualSummary {
  locationBased: { total: number; unit: string };
  marketBased: { total: number; unit: string };
  activitiesCount: number;
  coveredByInstruments: number;
  supplementary: Scope2SupplementaryTotals;
}

// Generated report record
export interface GeneratedReport {
  _id: string;
  initiativeId: string;
  name: string;
  templateType: ReportTemplateType;
  status: GeneratedReportStatus;
  periodStart: string;
  periodEnd: string;
  fileUrl?: string;
  fileSize?: number;
  progress?: number;
  error?: string;
  createdBy: ActivityUser | string;
  created: string;
  updated: string;
}

// PPTX report item returned from api.g17.eco BackgroundJob model
export interface EmissionsPPTXReportItem {
  _id: string;
  status: 'pending' | 'processing' | 'waitForRun' | 'paused' | 'completed' | 'error' | 'deleted';
  created: string;
  user?: { _id: string; firstName?: string; surname?: string };
  initiativeId: string;
  taskId: string;
  periodStart?: string;
  periodEnd?: string;
  exportedFilePath?: string;
}

// Emissions reduction target
export interface EmissionsTarget {
  _id: string;
  initiativeId: string;
  rootInitiativeId: string;
  name: string;
  framework: TargetFramework;
  reductionPercentage: number;
  baselineYear: number;
  targetYear: number;
  baselineEmissions?: number;
  targetEmissions?: number;
  currentEmissions?: number;
  progress?: number;
  status: TargetStatus;
  notes?: string;
  createdBy: ActivityUser | string;
  created: string;
  updated: string;
}

// Create target request
export interface CreateTargetRequest {
  name?: string;
  framework: TargetFramework;
  reductionPercentage: number;
  baselineYear: number;
  targetYear: number;
  notes?: string;
}

// Update target request
export type UpdateTargetRequest = Partial<CreateTargetRequest>;

// Intensity metrics for carbon intensity calculations
export interface IntensityMetrics {
  totalEmissions: number;
  perEmployee: number | null;
  perRevenue: number | null;
  perFloorArea: number | null;
  businessMetrics: {
    employeeCount: number | null;
    employeeCountType: EmployeeCountType | null;
    revenue: number | null;
    revenueCurrency: string | null;
    floorArea: number | null;
    floorAreaUnit: FloorAreaUnit | null;
  };
  period: { start: string; end: string };
  dataSource: string | null;
}

// Business metrics record stored in database
export interface BusinessMetrics {
  _id: string;
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  employeeCount?: number;
  employeeCountType?: EmployeeCountType;
  revenue?: number;
  revenueCurrency?: string;
  floorArea?: number;
  floorAreaUnit?: FloorAreaUnit;
  notes?: string;
  dataSource?: string;
  createdBy: string;
  updatedBy?: string;
  created: string;
  updated: string;
}

// Request to create/update business metrics
export interface BusinessMetricsRequest {
  periodStart: string;
  periodEnd: string;
  employeeCount?: number;
  employeeCountType?: EmployeeCountType;
  revenue?: number;
  revenueCurrency?: string;
  floorArea?: number;
  floorAreaUnit?: FloorAreaUnit;
  notes?: string;
  dataSource?: string;
}

// Intensity treemap data
export interface IntensityTreemapItem {
  _id: string;
  name: string;
  code?: string;
  totalCo2e: number;
  totalActivities: number;
  businessMetrics: {
    employeeCount: number | null;
    revenue: number | null;
    floorArea: number | null;
    floorAreaUnit: string | null;
  } | null;
}

export interface IntensityTreemapResponse {
  initiatives: IntensityTreemapItem[];
  period: { start: string; end: string };
  summary: { totalInitiatives: number; totalCo2e: number };
}

// Emission factor source (lowercase string from API)
export type EmissionFactorSource = string;

// Emission factor source definition with metadata
export interface EmissionFactorSourceDefinition {
  value: string;
  label: string;
  fullName: string;
  description?: string;
  url?: string;
  region?: string;
}

// Emission factor from database
export interface EmissionFactor {
  _id: string;
  name: string;
  description?: string;
  provider: EmissionFactorProvider;
  source: EmissionFactorSource;
  sourceId?: string;
  scope: EmissionScope;
  activityType: string;
  region: string;
  regionName: string;
  fuelType?: string;
  category: string;
  subcategory?: string;
  factor: number;
  factorUnit: string;
  activityUnit: string;
  gwpMethodology: string;
  year: number;
  co2Factor?: number;
  ch4Factor?: number;
  n2oFactor?: number;
  isActive: boolean;
  created: string;
  updated: string;
}

// Region option for filter dropdown
export interface RegionOption {
  code: string;
  name: string;
}

// Alternative Climatiq factor stored in the search cache alongside the selected factor
export interface AlternativeFactor {
  id: string;
  activity_id: string;
  name: string;
  region: string;
  source: string;
  year: number;
  unit: string;
  factor: number | null;
}

// Emission factors list response
export interface EmissionFactorsListResponse {
  sources: EmissionFactorSourceDefinition[];
  regions: RegionOption[];
  years: number[];
  categories: string[];
  emissionFactors: EmissionFactor[];
  pagination: Pagination;
}

// Emission factors query params
export interface EmissionFactorsQueryParams {
  query?: string;
  scope?: EmissionScope;
  activityType?: string;
  region?: string;
  fuelType?: string;
  source?: EmissionFactorSource;
  year?: number;
  limit?: number;
  offset?: number;
}

// Facilities comparison
export interface FacilityEmissionData {
  _id: string;
  name?: string;
  code?: string;
  scopes: Array<{ scope: EmissionScope | 'scope3'; co2e: number; activityCount: number }>;
  activityTypes?: ActivityTypeBreakdown[];
  totalCo2e: number;
  totalActivities: number;
}

export interface FacilitiesComparisonResponse {
  facilities: FacilityEmissionData[];
  period: { start: string | Date; end: string | Date };
  summary: { totalFacilities: number; totalCo2e: number; unit: string };
}

export interface FacilitiesComparisonParams {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  groupBy?: 'scope' | 'activityType' | 'month';
  includeChildren?: boolean;
}

// ============================================================================
// Settings Types
// ============================================================================

export type GWPMethodology = 'AR4' | 'AR5' | 'AR6';
export type OrganizationalBoundary = 'operational_control' | 'financial_control' | 'equity_share';
export type EmissionsDisplayUnit = 'kgCO2e' | 'tCO2e' | 'short_ton';

export interface EmissionSettings {
  _id: string;
  initiativeId: string;
  gwpMethodology: GWPMethodology;
  defaultRegion: string;
  organizationalBoundary: OrganizationalBoundary;
  baseYear: number;
  displayUnit: EmissionsDisplayUnit;
  decimalPlaces: number;
  currency: string;
  created: string;
  updated: string;
}

export interface CalculationSettings {
  gwpMethodology: GWPMethodology;
  defaultRegion: string;
}

export interface ReportingSettings {
  organizationalBoundary: OrganizationalBoundary;
  baseYear: number;
}

export interface DisplaySettings {
  displayUnit: EmissionsDisplayUnit;
  decimalPlaces: number;
  currency: string;
}

export interface UpdateCalculationSettingsRequest {
  gwpMethodology?: GWPMethodology;
  defaultRegion?: string;
}

export interface UpdateReportingSettingsRequest {
  organizationalBoundary?: OrganizationalBoundary;
  baseYear?: number;
}

export interface UpdateDisplaySettingsRequest {
  displayUnit?: EmissionsDisplayUnit;
  decimalPlaces?: number;
  currency?: string;
}

export interface UpdateAllSettingsRequest {
  gwpMethodology?: GWPMethodology;
  defaultRegion?: string;
  organizationalBoundary?: OrganizationalBoundary;
  baseYear?: number;
  displayUnit?: EmissionsDisplayUnit;
  decimalPlaces?: number;
  currency?: string;
}

// ============================================================================
// Onboarding Types
// ============================================================================

export enum OnboardingStatus {
  NotStarted = 'not-started',
  InProgress = 'in-progress',
  Completed = 'completed',
}

export interface OnboardingItems {
  hasBaseYear: boolean;
  hasAssets: boolean;
  hasActivities: boolean;
  hasTarget: boolean;
}

export interface OnboardingStatusResponse {
  status: OnboardingStatus;
  items: OnboardingItems;
}

// ============================================================================
// Emission Factor Import Types
// ============================================================================

export interface EmissionFactorImportRowResult {
  row: number;
  success: boolean;
  data?: EmissionFactor;
  errors?: string[];
}

export interface EmissionFactorImportSummary {
  total: number;
  successful: number;
  failed: number;
}

export interface EmissionFactorImportResult {
  success: boolean;
  summary: EmissionFactorImportSummary;
  results: EmissionFactorImportRowResult[];
}

// ============================================================================
// Bulk Import Types
// ============================================================================

export interface BulkImportRowResult {
  index: number;
  success: boolean;
  activityRecordId?: string;
  error?: string;
  warning?: string;
}

export enum BulkImportType {
  Activity = 'activity',
  Asset = 'asset',
}

export interface BulkImport {
  _id: string;
  initiativeId: string;
  createdBy: string;
  importType: BulkImportType;
  scope?: EmissionScope;
  activityType?: string;
  assetType?: string;
  status: BulkImportStatus;
  fileId: string;
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  activityRecordIds: string[];
  assetIds?: string[];
  rowResults: BulkImportRowResult[];
  regionFallback: boolean;
  yearFallback: boolean;
  created: string;
  updated: string;
}

// ============================================================================
// Asset Types (discriminated union)
// ============================================================================

export interface EmissionAssetBase {
  _id: string;
  initiativeId: string;
  name: string;
  assetType: AssetType;
  description?: string;
  address?: string;
  region?: string;
  activityCount: number;
  totalCo2e: number;
  /**
   * Number of similar units this asset stands for (e.g. 20 vans in a fleet,
   * 5 identical generators, 3 facilities on a campus). Undefined or 1 ≡ a
   * single unit. ≥ 2 means a group: activities log the bundled total.
   */
  unitCount?: number;
  customIntensityLabel?: string;
  customIntensityValue?: number;
  created: string;
  updated: string;
}

export interface FacilityAsset extends EmissionAssetBase {
  assetType: AssetType.Facility;
  floorAreaM2?: number;
  employeeCount?: number;
}

export interface VehicleAsset extends EmissionAssetBase {
  assetType: AssetType.Vehicle;
  vehicleType: VehicleType;
  identifier?: string;
}

export interface DataCentreAsset extends EmissionAssetBase {
  assetType: AssetType.DataCentre;
  pue?: number;
  itLoadKw?: number;
  rackCount?: number;
}

export interface ManufacturingFacilityAsset extends EmissionAssetBase {
  assetType: AssetType.ManufacturingFacility;
  floorAreaM2?: number;
  employeeCount?: number;
}

export interface EquipmentAsset extends EmissionAssetBase {
  assetType: AssetType.Equipment;
  operatingHoursPerYear?: number;
  ratedCapacityKw?: number;
}

export interface OtherAsset extends EmissionAssetBase {
  assetType: AssetType.Other;
}

export type EmissionAsset =
  | FacilityAsset
  | VehicleAsset
  | DataCentreAsset
  | ManufacturingFacilityAsset
  | EquipmentAsset
  | OtherAsset;

export interface CreateAssetRequest {
  name: string;
  assetType: AssetType;
  description?: string;
  address?: string;
  region?: string;
  floorAreaM2?: number;
  employeeCount?: number;
  vehicleType?: VehicleType;
  identifier?: string;
  pue?: number;
  itLoadKw?: number;
  rackCount?: number;
  operatingHoursPerYear?: number;
  ratedCapacityKw?: number;
  unitCount?: number;
  customIntensityValue?: number;
  customIntensityLabel?: string;
}

export type UpdateAssetRequest = Omit<Partial<CreateAssetRequest>, 'assetType'>;

export interface AssetListResponse {
  data: EmissionAsset[];
  pagination: Pagination;
}

export type AssetDeleteMode = 'delete_activities' | 'detach_activities';

export interface AssetSummary {
  totalCO2e: number;
  activityCount: number;
  byScope: Record<string, number>;
  intensity?: {
    value: number;
    unit: string;
    denominator: string;
  };
}

export interface ActivityTypeBreakdown {
  activityType: string;
  co2e: number;
  count: number;
}

export interface AssetComparisonItem {
  _id: string;
  name: string;
  assetType: AssetType;
  scopes: Array<{
    scope: string;
    co2e: number;
    activityCount: number;
  }>;
  activityTypes: ActivityTypeBreakdown[];
  totalCo2e: number;
  totalActivities: number;
}

export interface AssetComparisonResponse {
  assets: AssetComparisonItem[];
  period: { start: string; end: string };
  summary: {
    totalAssets: number;
    totalCo2e: number;
  };
}
